/**
 * Taiwan Central Weather Administration (CWA) Weather API Service
 * API Documentation: https://opendata.cwa.gov.tw/dist/opendata-swagger.html
 */

const CWA_API_KEY = 'CWA-07B5B879-B086-421E-B942-027892867DB0';
// 一般天氣預報-36小時（縣市層級，用於當前天氣）
const CWA_ENDPOINT_36H = 'https://opendata.cwa.gov.tw/api/v1/rest/datastore/F-C0032-001';
// 臺灣各鄉鎮市區未來2天逐時+1週3小時預報（用於 hourly forecast）
// Use county-specific dataset when available; fallback to Taiwan-wide dataset.
const CWA_HOURLY_DATASET_BY_COUNTY: Record<string, string> = {
  '宜蘭縣': 'F-D0047-003',
  '桃園市': 'F-D0047-007',
  '新竹縣': 'F-D0047-011',
  '苗栗縣': 'F-D0047-015',
  '彰化縣': 'F-D0047-019',
  '南投縣': 'F-D0047-023',
  '雲林縣': 'F-D0047-027',
  '嘉義縣': 'F-D0047-031',
  '屏東縣': 'F-D0047-035',
  '臺東縣': 'F-D0047-039',
  '花蓮縣': 'F-D0047-043',
  '澎湖縣': 'F-D0047-047',
  '基隆市': 'F-D0047-051',
  '新竹市': 'F-D0047-055',
  '嘉義市': 'F-D0047-059',
  '臺北市': 'F-D0047-061',
  '高雄市': 'F-D0047-067',
  '新北市': 'F-D0047-071',
  '臺中市': 'F-D0047-075',
  '臺南市': 'F-D0047-079',
  '連江縣': 'F-D0047-083',
  '金門縣': 'F-D0047-087',
  '臺灣': 'F-D0047-091',
  '台灣': 'F-D0047-091',
};
// 現在天氣觀測報告-有人氣象站資料（用於當前溫度和今日高低溫）
const CWA_ENDPOINT_OBS = 'https://opendata.cwa.gov.tw/api/v1/rest/datastore/O-A0003-001';

// Taiwan counties/cities list
export const TAIWAN_LOCATIONS = [
  '臺北市',
  '新北市',
  '桃園市',
  '臺中市',
  '臺南市',
  '高雄市',
  '基隆市',
  '新竹市',
  '嘉義市',
  '新竹縣',
  '苗栗縣',
  '彰化縣',
  '南投縣',
  '雲林縣',
  '嘉義縣',
  '屏東縣',
  '宜蘭縣',
  '花蓮縣',
  '臺東縣',
  '澎湖縣',
  '金門縣',
  '連江縣',
] as const;

export type TaiwanLocation = typeof TAIWAN_LOCATIONS[number];

const IP_GEO_ENDPOINT = 'https://ipapi.co/json/';
const IP_REGION_TO_TAIWAN_LOCATION: Record<string, TaiwanLocation> = {
  'Taipei City': '臺北市',
  'New Taipei City': '新北市',
  'Taoyuan City': '桃園市',
  'Taichung City': '臺中市',
  'Tainan City': '臺南市',
  'Kaohsiung City': '高雄市',
  'Keelung City': '基隆市',
  'Hsinchu City': '新竹市',
  'Chiayi City': '嘉義市',
  'Hsinchu County': '新竹縣',
  'Miaoli County': '苗栗縣',
  'Changhua County': '彰化縣',
  'Nantou County': '南投縣',
  'Yunlin County': '雲林縣',
  'Chiayi County': '嘉義縣',
  'Pingtung County': '屏東縣',
  'Yilan County': '宜蘭縣',
  'Hualien County': '花蓮縣',
  'Taitung County': '臺東縣',
  'Penghu County': '澎湖縣',
  'Kinmen County': '金門縣',
  'Lienchiang County': '連江縣',
  'Lianjiang County': '連江縣',
};

const PREFERRED_TOWN_BY_COUNTY: Record<string, string[]> = {
  '臺北市': ['中正區', '大安區', '信義區', '松山區', '內湖區'],
  '新北市': ['板橋區', '新莊區', '中和區', '永和區', '新店區'],
  '臺中市': ['西屯區', '北區', '西區', '南屯區'],
  '臺南市': ['中西區', '東區', '北區', '永康區'],
  '高雄市': ['前金區', '苓雅區', '左營區', '三民區'],
};

// Forecast data point for a specific time period (from F-C0032-001)
export interface ForecastPoint {
  startTime: Date;
  endTime: Date;
  minTemp: number | null;
  maxTemp: number | null;
  avgTemp: number | null;
  description: string;
  rainProbability: string;
  comfortLevel: string;
}

// Hourly temperature forecast point (from F-D0047-089)
export interface HourlyForecastPoint {
  time: Date;
  temperature: number;
  humidity?: number;
  apparentTemperature?: number;
  description?: string;
}

// Current observation data (from O-A0003-001)
export interface ObservationData {
  stationName: string;
  stationId: string;
  countyName: string;
  townName: string;
  currentTemp: number;
  humidity: number;
  observationTime: Date;
  dailyHigh?: { temp: number; time: Date };
  dailyLow?: { temp: number; time: Date };
}

export interface WeatherData {
  locationName: string;
  temperature: number | null;
  temperatureUnit: string;
  description: string;
  humidity: string;
  comfortLevel: string;
  rainProbability: string;
  fetchTime: Date;
  // Forecast data (multiple time periods from CWA API F-C0032-001)
  forecast?: ForecastPoint[];
  // Hourly forecast data (from F-D0047-089) - up to 48+ hours
  hourlyForecast?: HourlyForecastPoint[];
  // Current observation data (from O-A0003-001) - includes daily high/low for interpolation
  observation?: ObservationData;
}

interface CWAWeatherElement {
  elementName: string;
  time: Array<{
    startTime: string;
    endTime: string;
    parameter: {
      parameterName: string;
      parameterValue?: string;
      parameterUnit?: string;
    };
  }>;
}

interface CWALocationData {
  locationName: string;
  weatherElement: CWAWeatherElement[];
}

interface CWAResponse {
  success: string;
  result: {
    resource_id: string;
    fields: any[];
  };
  records: {
    datasetDescription: string;
    location: CWALocationData[];
  };
}

/**
 * Fetch current observation from O-A0003-001
 * Returns the observation from the station closest to the specified county/city
 */
async function fetchObservation(locationName: string): Promise<ObservationData | null> {
  try {
    const url = new URL(CWA_ENDPOINT_OBS);
    url.searchParams.append('Authorization', CWA_API_KEY);
    
    const response = await fetch(url.toString());
    if (!response.ok) {
      console.error(`[WeatherAPI] Observation HTTP error: ${response.status}`);
      return null;
    }
    
    const data = await response.json();
    
    if (data.success !== 'true' || !data.records?.Station) {
      console.error('[WeatherAPI] No observation data');
      return null;
    }
    
    // Find station matching the county/city name
    const stations = data.records.Station;
    console.log(`[WeatherAPI] Observation: ${stations.length} stations available, searching for: ${locationName}`);
    
    // Normalize location name for matching (台/臺)
    const normalizeLocation = (name: string) => name?.replace(/台/g, '臺');
    const normalizedLocationName = normalizeLocation(locationName);
    
    // Filter candidates by county
    const countyCandidates = stations.filter((s: any) =>
      normalizeLocation(s.GeoInfo?.CountyName) === normalizedLocationName
    );
    const candidates = countyCandidates.length > 0 ? countyCandidates : stations;
    
    let targetStation: any | undefined;
    
    // Prefer configured town list for this county (avoid arbitrary town like 北投區)
    const preferredTowns = PREFERRED_TOWN_BY_COUNTY[normalizedLocationName] || [];
    if (preferredTowns.length > 0) {
      targetStation = candidates.find((s: any) =>
        preferredTowns.includes(normalizeLocation(s.GeoInfo?.TownName))
      );
    }
    
    // Try exact county match
    if (!targetStation) {
      targetStation = candidates.find((s: any) =>
        normalizeLocation(s.GeoInfo?.CountyName) === normalizedLocationName
      );
    }
    
    // Fallback: find by partial match
    if (!targetStation) {
      targetStation = candidates.find((s: any) =>
        normalizeLocation(s.GeoInfo?.CountyName)?.includes(normalizedLocationName) ||
        normalizedLocationName?.includes(normalizeLocation(s.GeoInfo?.CountyName))
      );
    }
    
    // Still not found? Use first candidate as fallback
    if (!targetStation) {
      targetStation = candidates[0];
      console.warn(`[WeatherAPI] Station not found, using fallback: ${targetStation?.StationName} in ${targetStation?.GeoInfo?.CountyName} (requested: ${locationName})`);
    } else {
      console.log(`[WeatherAPI] Found station: ${targetStation.StationName} in ${targetStation.GeoInfo?.CountyName} (${targetStation.GeoInfo?.TownName})`);
    }
    
    if (!targetStation) return null;
    
    const weatherElement = targetStation.WeatherElement;
    const geoInfo = targetStation.GeoInfo;
    const obsTime = new Date(targetStation.ObsTime?.DateTime);
    
    // Parse daily extreme temperatures
    let dailyHigh: { temp: number; time: Date } | undefined;
    let dailyLow: { temp: number; time: Date } | undefined;
    
    if (weatherElement?.DailyExtreme?.DailyHigh?.TemperatureInfo) {
      const highInfo = weatherElement.DailyExtreme.DailyHigh.TemperatureInfo;
      dailyHigh = {
        temp: parseFloat(highInfo.AirTemperature),
        time: new Date(highInfo.Occurred_at?.DateTime),
      };
    }
    
    if (weatherElement?.DailyExtreme?.DailyLow?.TemperatureInfo) {
      const lowInfo = weatherElement.DailyExtreme.DailyLow.TemperatureInfo;
      dailyLow = {
        temp: parseFloat(lowInfo.AirTemperature),
        time: new Date(lowInfo.Occurred_at?.DateTime),
      };
    }
    
    const observation: ObservationData = {
      stationName: targetStation.StationName,
      stationId: targetStation.StationId,
      countyName: geoInfo?.CountyName || '',
      townName: geoInfo?.TownName || '',
      currentTemp: parseFloat(weatherElement?.AirTemperature || '0'),
      humidity: parseFloat(weatherElement?.RelativeHumidity || '0'),
      observationTime: obsTime,
      dailyHigh,
      dailyLow,
    };
    
    console.log(`[WeatherAPI] Observation: ${observation.stationName} - ${observation.currentTemp}°C, High: ${dailyHigh?.temp}°C @ ${dailyHigh?.time?.getHours()}:00, Low: ${dailyLow?.temp}°C @ ${dailyLow?.time?.getHours()}:00`);
    
    return observation;
  } catch (error) {
    console.error('[WeatherAPI] Failed to fetch observation:', error);
    return null;
  }
}

/**
 * Fetch hourly forecast from F-D0047-089
 */
async function fetchHourlyForecast(
  countyName: string,
  townName?: string
): Promise<HourlyForecastPoint[]> {
  try {
    const normalizeName = (name: string) => name?.replace(/台/g, '臺');
    const normalizedCounty = normalizeName(countyName);
    const normalizedTown = townName ? normalizeName(townName) : undefined;
    const datasetId = CWA_HOURLY_DATASET_BY_COUNTY[normalizedCounty] || CWA_HOURLY_DATASET_BY_COUNTY['台灣'];
    const url = new URL(`https://opendata.cwa.gov.tw/api/v1/rest/datastore/${datasetId}`);
    url.searchParams.append('Authorization', CWA_API_KEY);
    
    const response = await fetch(url.toString());
    if (!response.ok) {
      console.error(`[WeatherAPI] Hourly forecast HTTP error: ${response.status}`);
      return [];
    }
    
    const data = await response.json();
    
    if (data.success !== 'true' || !data.records?.Locations?.[0]?.Location) {
      console.error('[WeatherAPI] No hourly forecast data');
      return [];
    }
    
    // Find the matching location (township if available, otherwise county)
    const locations = data.records.Locations[0].Location;
    console.log(`[WeatherAPI] Hourly forecast dataset ${datasetId}: ${locations.length} locations, county=${normalizedCounty}, town=${normalizedTown || '-'}`);

    let targetLocation: any | undefined;

    if (normalizedTown) {
      targetLocation = locations.find((loc: any) => normalizeName(loc.LocationName) === normalizedTown);
      if (!targetLocation) {
        targetLocation = locations.find((loc: any) =>
          normalizeName(loc.LocationName)?.includes(normalizedTown) || normalizedTown.includes(normalizeName(loc.LocationName))
        );
      }
    }

    if (!targetLocation) {
      // Fallback to county match when dataset provides counties, or first location
      targetLocation = locations.find((loc: any) => normalizeName(loc.LocationName) === normalizedCounty);
    }

    if (!targetLocation) {
      targetLocation = locations[0];
      console.warn(`[WeatherAPI] Location not found, using fallback: ${targetLocation?.LocationName}`);
    } else {
      console.log(`[WeatherAPI] Found location: ${targetLocation.LocationName}`);
    }
    
    if (!targetLocation?.WeatherElement) {
      return [];
    }
    
    const hourlyData: HourlyForecastPoint[] = [];
    
    // Find temperature element (prefer hourly "溫度", fallback to "平均溫度")
    const tempElement =
      targetLocation.WeatherElement.find((e: any) => e.ElementName === '溫度') ||
      targetLocation.WeatherElement.find((e: any) => e.ElementName === '平均溫度');
    const humidityElement =
      targetLocation.WeatherElement.find((e: any) => e.ElementName === '相對濕度') ||
      targetLocation.WeatherElement.find((e: any) => e.ElementName === '平均相對濕度');
    const apparentTempElement =
      targetLocation.WeatherElement.find((e: any) => e.ElementName === '體感溫度');
    
    if (tempElement?.Time?.length) {
      const firstPoint = tempElement.Time[0];
      const hasDataTime = !!firstPoint?.DataTime;
      const hasInterval = !!firstPoint?.StartTime && !!firstPoint?.EndTime;
      
      if (hasDataTime) {
        for (const timePoint of tempElement.Time) {
          const dataTime = new Date(timePoint.DataTime);
          const temp = parseFloat(timePoint.ElementValue?.[0]?.Temperature || '0');
          
          // Find matching humidity and apparent temperature
          let humidity: number | undefined;
          let apparentTemp: number | undefined;
          
          if (humidityElement?.Time) {
            const humidityPoint = humidityElement.Time.find((h: any) => h.DataTime === timePoint.DataTime);
            if (humidityPoint?.ElementValue?.[0]?.RelativeHumidity) {
              humidity = parseFloat(humidityPoint.ElementValue[0].RelativeHumidity);
            }
          }
          
          if (apparentTempElement?.Time) {
            const apparentPoint = apparentTempElement.Time.find((a: any) => a.DataTime === timePoint.DataTime);
            if (apparentPoint?.ElementValue?.[0]?.ApparentTemperature) {
              apparentTemp = parseFloat(apparentPoint.ElementValue[0].ApparentTemperature);
            }
          }
          
          hourlyData.push({
            time: dataTime,
            temperature: temp,
            humidity,
            apparentTemperature: apparentTemp,
          });
        }
      } else if (hasInterval) {
        console.warn(`[WeatherAPI] Hourly data not available for ${targetLocation.LocationName}; using interval temperature from ${tempElement.ElementName}`);
        for (const timePoint of tempElement.Time) {
          const startTime = new Date(timePoint.StartTime);
          const endTime = new Date(timePoint.EndTime);
          const temp = parseFloat(timePoint.ElementValue?.[0]?.Temperature || '0');
          
          const cursor = new Date(startTime);
          while (cursor < endTime) {
            hourlyData.push({
              time: new Date(cursor),
              temperature: temp,
            });
            cursor.setHours(cursor.getHours() + 1);
          }
        }
      }
    }
    
    console.log(`[WeatherAPI] Fetched ${hourlyData.length} hourly forecast points`);
    return hourlyData;
  } catch (error) {
    console.error('[WeatherAPI] Failed to fetch hourly forecast:', error);
    return [];
  }
}

/**
 * Fetch weather data for a specific location
 */
export async function getWeatherByLocation(locationName: string): Promise<WeatherData | null> {
  try {
    // Fetch observation first to get township for more accurate hourly forecast
    const observation = await fetchObservation(locationName);

    // Fetch 36-hour forecast and hourly forecast
    const [basicWeatherPromise, hourlyForecastPromise] = await Promise.allSettled([
      fetch((() => {
        const url = new URL(CWA_ENDPOINT_36H);
        url.searchParams.append('Authorization', CWA_API_KEY);
        url.searchParams.append('locationName', locationName);
        return url.toString();
      })()),
      fetchHourlyForecast(locationName, observation?.townName),
    ]);
    
    // Process basic weather data
    let basicWeather: WeatherData | null = null;
    
    if (basicWeatherPromise.status === 'fulfilled') {
      const response = basicWeatherPromise.value;
      if (response.ok) {
        const data: CWAResponse = await response.json();
        
        if (data.success === 'true' && data.records?.location?.length) {
          const location = data.records.location[0];
          const weatherElements = location.weatherElement;

          const getElementValue = (elementName: string, timeIndex: number = 0): string => {
            const element = weatherElements.find(e => e.elementName === elementName);
            if (element && element.time.length > timeIndex) {
              return element.time[timeIndex].parameter.parameterName;
            }
            return '';
          };
          
          const getElementTime = (elementName: string, timeIndex: number = 0): { startTime: string; endTime: string } | null => {
            const element = weatherElements.find(e => e.elementName === elementName);
            if (element && element.time.length > timeIndex) {
              return {
                startTime: element.time[timeIndex].startTime,
                endTime: element.time[timeIndex].endTime,
              };
            }
            return null;
          };

          const minTemp = getElementValue('MinT');
          const maxTemp = getElementValue('MaxT');
          
          let temperature: number | null = null;
          if (minTemp && maxTemp) {
            temperature = Math.round((parseInt(minTemp) + parseInt(maxTemp)) / 2);
          } else if (minTemp) {
            temperature = parseInt(minTemp);
          } else if (maxTemp) {
            temperature = parseInt(maxTemp);
          }
          
          // Extract forecast data (all time periods)
          const forecast: ForecastPoint[] = [];
          const minTElement = weatherElements.find(e => e.elementName === 'MinT');
          const numPeriods = minTElement?.time.length || 0;
          
          for (let i = 0; i < numPeriods; i++) {
            const timeInfo = getElementTime('MinT', i);
            if (!timeInfo) continue;
            
            const periodMinTemp = getElementValue('MinT', i);
            const periodMaxTemp = getElementValue('MaxT', i);
            
            let minT: number | null = periodMinTemp ? parseInt(periodMinTemp) : null;
            let maxT: number | null = periodMaxTemp ? parseInt(periodMaxTemp) : null;
            let avgT: number | null = null;
            
            if (minT !== null && maxT !== null) {
              avgT = Math.round((minT + maxT) / 2);
            } else if (minT !== null) {
              avgT = minT;
            } else if (maxT !== null) {
              avgT = maxT;
            }
            
            forecast.push({
              startTime: new Date(timeInfo.startTime),
              endTime: new Date(timeInfo.endTime),
              minTemp: minT,
              maxTemp: maxT,
              avgTemp: avgT,
              description: getElementValue('Wx', i),
              rainProbability: getElementValue('PoP', i) + '%',
              comfortLevel: getElementValue('CI', i),
            });
          }

          basicWeather = {
            locationName: location.locationName,
            temperature,
            temperatureUnit: '°C',
            description: getElementValue('Wx'),
            humidity: '',
            comfortLevel: getElementValue('CI'),
            rainProbability: getElementValue('PoP') + '%',
            fetchTime: new Date(),
            forecast,
          };
        }
      }
    }
    
    if (!basicWeather) {
      console.error('[WeatherAPI] No data returned for location:', locationName);
      return null;
    }
    
    // Build hourly cache points from forecast + observation
    const cachePoints: { ts: number; temperature: number; source: 'forecast' | 'observation' }[] = [];
    if (hourlyForecastPromise.status === 'fulfilled') {
      hourlyForecastPromise.value.forEach((p) => {
        cachePoints.push({
          ts: roundToHourTs(p.time),
          temperature: p.temperature,
          source: 'forecast',
        });
      });
    }
    if (observation) {
      cachePoints.push({
        ts: roundToHourTs(observation.observationTime),
        temperature: observation.currentTemp,
        source: 'observation',
      });
    }

    const mergedHourly = updateHourlyCache(locationName, cachePoints);
    basicWeather.hourlyForecast = mergedHourly.map((p) => ({
      time: new Date(p.ts),
      temperature: p.temperature,
    }));
    
    // Add observation data if available (priority for current temperature)
    if (observation) {
      basicWeather.observation = observation;
      // Use observation for current temperature (more accurate than forecast)
      basicWeather.temperature = observation.currentTemp;
      console.log(`[WeatherAPI] Using observation temperature: ${observation.currentTemp}°C from ${observation.stationName}`);
    } else if (basicWeather.hourlyForecast && basicWeather.hourlyForecast.length > 0) {
      // Fallback to hourly forecast for current temperature
      const now = new Date();
      const currentHourForecast = basicWeather.hourlyForecast.find((h) => {
        const hourDiff = Math.abs(h.time.getTime() - now.getTime()) / (1000 * 60 * 60);
        return hourDiff < 1; // Within 1 hour
      });
      
      if (currentHourForecast) {
        basicWeather.temperature = currentHourForecast.temperature;
        console.log(`[WeatherAPI] Using hourly forecast temperature: ${currentHourForecast.temperature}°C`);
      }
    }
    
    return basicWeather;
  } catch (error) {
    console.error('[WeatherAPI] Failed to fetch weather:', error);
    return null;
  }
}

/**
 * Fetch weather data for all locations
 */
export async function getAllWeatherData(): Promise<Map<string, WeatherData>> {
  const weatherMap = new Map<string, WeatherData>();

  try {
    const url = new URL(CWA_ENDPOINT_36H);
    url.searchParams.append('Authorization', CWA_API_KEY);

    const response = await fetch(url.toString());
    
    if (!response.ok) {
      console.error(`[WeatherAPI] HTTP error: ${response.status}`);
      return weatherMap;
    }

    const data: CWAResponse = await response.json();

    if (data.success !== 'true' || !data.records?.location) {
      return weatherMap;
    }

    for (const location of data.records.location) {
      const weatherElements = location.weatherElement;

      const getElementValue = (elementName: string): string => {
        const element = weatherElements.find(e => e.elementName === elementName);
        if (element && element.time.length > 0) {
          return element.time[0].parameter.parameterName;
        }
        return '';
      };

      const minTemp = getElementValue('MinT');
      const maxTemp = getElementValue('MaxT');
      
      let temperature: number | null = null;
      if (minTemp && maxTemp) {
        temperature = Math.round((parseInt(minTemp) + parseInt(maxTemp)) / 2);
      } else if (minTemp) {
        temperature = parseInt(minTemp);
      } else if (maxTemp) {
        temperature = parseInt(maxTemp);
      }

      weatherMap.set(location.locationName, {
        locationName: location.locationName,
        temperature,
        temperatureUnit: '°C',
        description: getElementValue('Wx'),
        humidity: '',
        comfortLevel: getElementValue('CI'),
        rainProbability: getElementValue('PoP') + '%',
        fetchTime: new Date(),
      });
    }
  } catch (error) {
    console.error('[WeatherAPI] Failed to fetch all weather data:', error);
  }

  return weatherMap;
}

// LocalStorage key for persisting selected location
export const WEATHER_LOCATION_KEY = 'hvac-weather-location';

/**
 * Get saved location from localStorage
 */
export function getSavedLocation(): string | null {
  try {
    return localStorage.getItem(WEATHER_LOCATION_KEY);
  } catch {
    return null;
  }
}

/**
 * Save location to localStorage
 */
export function saveLocation(locationName: string): void {
  try {
    localStorage.setItem(WEATHER_LOCATION_KEY, locationName);
  } catch (error) {
    console.error('[WeatherAPI] Failed to save location:', error);
  }
}

const WEATHER_HOURLY_CACHE_KEY = 'hvac-weather-hourly-cache';

type CachedHourlyPoint = {
  ts: number;
  temperature: number;
  source: 'forecast' | 'observation';
};

type WeatherHourlyCache = Record<string, CachedHourlyPoint[]>;

const roundToHourTs = (date: Date): number => {
  const d = new Date(date);
  d.setMinutes(0, 0, 0);
  return d.getTime();
};

const loadHourlyCache = (): WeatherHourlyCache => {
  try {
    const raw = localStorage.getItem(WEATHER_HOURLY_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const saveHourlyCache = (cache: WeatherHourlyCache): void => {
  try {
    localStorage.setItem(WEATHER_HOURLY_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error('[WeatherAPI] Failed to save hourly cache:', error);
  }
};

const updateHourlyCache = (locationName: string, points: CachedHourlyPoint[]): CachedHourlyPoint[] => {
  if (!locationName) return [];
  const cache = loadHourlyCache();
  const existing = Array.isArray(cache[locationName]) ? cache[locationName] : [];
  const byTs = new Map<number, CachedHourlyPoint>();
  existing.forEach((p) => {
    if (p && typeof p.ts === 'number') {
      byTs.set(p.ts, p);
    }
  });
  points.forEach((p) => {
    if (!p || typeof p.ts !== 'number') return;
    const prev = byTs.get(p.ts);
    if (!prev) {
      byTs.set(p.ts, p);
      return;
    }
    // Prefer forecast over observation for the same hour
    if (prev.source === 'observation' && p.source === 'forecast') {
      byTs.set(p.ts, p);
      return;
    }
    // Keep existing forecast if observation arrives for the same hour
    if (prev.source === 'forecast' && p.source === 'observation') {
      return;
    }
    // Default: replace with latest
    byTs.set(p.ts, p);
  });
  const now = Date.now();
  const cutoff = now - 48 * 60 * 60 * 1000;
  const merged = Array.from(byTs.values())
    .filter((p) => p.ts >= cutoff)
    .sort((a, b) => a.ts - b.ts);
  cache[locationName] = merged;
  saveHourlyCache(cache);
  return merged;
};

const getHourlyCache = (locationName: string): CachedHourlyPoint[] => {
  const cache = loadHourlyCache();
  return Array.isArray(cache[locationName]) ? cache[locationName] : [];
};

/**
 * Resolve Taiwan location by IP geolocation.
 */
export async function getLocationByIp(): Promise<TaiwanLocation | null> {
  try {
    const response = await fetch(IP_GEO_ENDPOINT);
    if (!response.ok) {
      console.error(`[WeatherAPI] IP geolocation HTTP error: ${response.status}`);
      return null;
    }
    const data: any = await response.json();
    if (data?.country_code !== 'TW') {
      return null;
    }

    const region = data?.region || data?.region_name || '';
    const city = data?.city || '';
    const normalizedRegion = region.trim();
    const normalizedCity = city.trim();

    if (TAIWAN_LOCATIONS.includes(normalizedRegion as TaiwanLocation)) {
      return normalizedRegion as TaiwanLocation;
    }
    if (TAIWAN_LOCATIONS.includes(normalizedCity as TaiwanLocation)) {
      return normalizedCity as TaiwanLocation;
    }

    if (IP_REGION_TO_TAIWAN_LOCATION[normalizedRegion]) {
      return IP_REGION_TO_TAIWAN_LOCATION[normalizedRegion];
    }

    return null;
  } catch (error) {
    console.error('[WeatherAPI] Failed to resolve location by IP:', error);
    return null;
  }
}
